import { useEffect, useState } from "react";
import { Cookie, Shield, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "bzv_cookie_consent";

export type ConsentChoice = "accepted" | "declined";

export function getStoredConsent(): ConsentChoice | null {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "accepted" || v === "declined") return v;
    return null;
  } catch {
    return null;
  }
}

function saveConsent(choice: ConsentChoice) {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Fallback: nothing we can do if localStorage is unavailable
  }
  // Notify listeners (analytics installer checks this)
  window.dispatchEvent(new CustomEvent("bzv:consent-change", { detail: choice }));
}

/**
 * LGPD-friendly cookie banner. Appears on first visit at the bottom-left of
 * the viewport, explains what we collect, and gives a clear accept/decline
 * choice. Choice is remembered in localStorage — the banner never reappears
 * unless the user clears site data.
 *
 * Side effect: fires a `bzv:consent-change` event so the analytics installer
 * can start/stop tracking in real time.
 */
export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show on admin routes — admins already understand the tracking
    if (typeof window === "undefined") return;
    if (window.location.pathname.startsWith("/admin")) return;

    const existing = getStoredConsent();
    if (!existing) {
      // Slight delay so the banner doesn't compete with first paint
      const id = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(id);
    }
  }, []);

  if (!visible) return null;

  const handleAccept = () => {
    saveConsent("accepted");
    setVisible(false);
  };

  const handleDecline = () => {
    saveConsent("declined");
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de cookies"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[60] animate-in fade-in slide-in-from-bottom-5 duration-500"
    >
      <div className="rounded-2xl border-2 border-border bg-card shadow-2xl overflow-hidden">
        <div className="bg-gradient-brand h-1" />
        <div className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Cookie className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-bold text-foreground leading-tight">
                Este site usa cookies
              </h2>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                Usamos cookies <strong className="text-foreground">essenciais</strong> pro checkout e carrinho, e{" "}
                <strong className="text-foreground">analíticos</strong> pra entender como você usa o site e melhorar sua experiência.
                Seus dados nunca são vendidos e respeitamos a{" "}
                <a
                  href="https://www.gov.br/anpd/pt-br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold underline"
                >
                  LGPD
                </a>
                .
              </p>
            </div>
            <button
              type="button"
              onClick={handleDecline}
              className="text-muted-foreground hover:text-foreground shrink-0"
              aria-label="Fechar e recusar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-[11px] text-muted-foreground">
            <Shield className="h-3.5 w-3.5 text-success shrink-0" />
            <span>
              Sem trackers de terceiros. Tudo hospedado na nossa infra.
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleAccept}
              className="flex-1 font-bold shadow-sm"
              size="sm"
            >
              Aceitar todos
            </Button>
            <Button
              onClick={handleDecline}
              variant="outline"
              size="sm"
              className="flex-1"
            >
              Apenas essenciais
            </Button>
          </div>

          <p className="text-[10px] text-center text-muted-foreground">
            Você pode mudar sua escolha a qualquer momento limpando os dados do site.
          </p>
        </div>
      </div>
    </div>
  );
}
